import { Controller } from "@hotwired/stimulus";
import consumer from "../channels/consumer";

function sanitizeCallerText(value) {
  const t = value == null ? "" : String(value).trim();
  if (!t) {
    return "";
  }
  if (/^unknown( caller)?$/i.test(t)) {
    return "";
  }
  return t;
}

function callerLinesFromData(data) {
  const num = sanitizeCallerText(data.from_number);
  const nam = sanitizeCallerText(data.customer_name);
  if (num) {
    return { primary: num, secondary: nam && nam !== num ? nam : "" };
  }
  if (nam) {
    return { primary: nam, secondary: "" };
  }
  return { primary: "", secondary: "" };
}

export default class extends Controller {
  static values = {
    organizationId: Number,
    organizationIdentifier: String,
    locale: String
  };

  static targets = ["notification", "notificationCard", "fromNumber", "customerName", "acceptButton", "declineButton"];

  connect() {
    if (!this.hasOrganizationIdValue) {
      console.warn("IncomingCall: No organization ID provided");
      return;
    }

    // Ensure notification starts hidden
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.add("hidden");
      this.notificationTarget.classList.remove("flex");
    }
    this.setModalCallerLines({ primary: "", secondary: "" });

    // Expose test function globally for console access in development
    if (typeof window !== "undefined") {
      window.testIncomingCall = () => {
        this.showIncomingCall({
          call_id: 999,
          from_number: "+1234567890",
          to_number: "+0987654321",
          customer_name: "Test Customer",
          organization_name: "Test Org"
        });
      };
    }

    // Subscribe to organization channel for incoming calls
    this.subscription = consumer.subscriptions.create(
      {
        channel: "OrganizationChannel",
        organization_id: this.organizationIdValue
      },
      {
        received: (data) => {
          this.handleMessage(data);
        },
        connected: () => {},
        disconnected: () => {},
        rejected: () => {
          console.error("IncomingCall: Subscription rejected - check authentication");
        }
      }
    );

    // Request browser notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }

  disconnect() {
    // Stop ringing sound when controller disconnects
    this.stopRingingSound();

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  handleMessage(data) {
    switch (data.type) {
    case "incoming_call":
      this.showIncomingCall(data);
      break;
    case "ai_transfer_request":
      // AI agent requested transfer to human - show as incoming call with transfer context
      this.showIncomingCall({
        ...data,
        is_transfer: true,
        transfer_reason: data.reason || "AI transfer request"
      });
      break;
    case "call_accepted":
      this.handleCallAccepted(data);
      break;
    case "call_declined":
      this.handleCallDeclined(data);
      break;
    case "call_accept_success":
      this.handleAcceptSuccess(data);
      break;
    case "call_accept_failed":
      this.handleAcceptFailed(data);
      break;
    case "call_ended":
    case "call_status_updated":
      this.handleCallEnded(data);
      break;
    }
  }

  showIncomingCall(data) {
    const currentUserId = this.getCurrentUserId();
    if (data.assigned_user_id && currentUserId && String(data.assigned_user_id) !== String(currentUserId)) {
      return;
    }

    this.currentCallId = data.call_id;

    const lines = callerLinesFromData(data);
    this.setModalCallerLines(lines);
    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.remove("hidden");
      this.notificationTarget.classList.add("flex");
    } else {
      console.error("IncomingCall: notification target not found");
    }

    this.playNotificationSound();
    this.showBrowserNotification(data);

    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
    }
    this.autoHideTimeout = setTimeout(() => {
      this.hideNotification();
    }, 30000);
  }

  handleCallAccepted(data) {
    if (data.call_id === this.currentCallId) {
      this.hideNotification();
      if (data.accepted_by !== this.getCurrentUserId()) {
        this.showMessage(`Call answered by ${data.accepted_by_name || "another agent"}`);
      }
    }
  }

  handleAcceptSuccess(data) {
    // This agent successfully accepted the call
    this.hideNotification();
    // Redirect to active call page
    const basePath = this.callsBasePath();
    window.location.href = `${basePath}/${data.call_id}/active`;
  }

  handleAcceptFailed(data) {
    // Call was already accepted by someone else
    this.hideNotification();
    this.showMessage(data.reason || "Call already answered by another agent");
  }

  handleCallDeclined(data) {
    if (data.call_id === this.currentCallId) {
      this.hideNotification();
    }
  }

  handleCallEnded(data) {
    if (data.call_id === this.currentCallId) {
      this.hideNotification();
      this.showMessage("Call ended by customer");
    }
  }

  async accept(event) {
    event.preventDefault();
    event.stopPropagation();

    const callId = event.target?.dataset?.callId || this.currentCallId;
    if (!callId) {
      console.error("IncomingCall: No call ID to accept");
      return;
    }

    if (this.hasAcceptButtonTarget && !event.target?.dataset?.callId) {
      this.acceptButtonTarget.disabled = true;
      this.acceptButtonTarget.textContent = "Connecting...";
    }
    if (this.hasDeclineButtonTarget && !event.target?.dataset?.callId) {
      this.declineButtonTarget.disabled = true;
    }

    try {
      const basePath = this.callsBasePath();
      const response = await fetch(`${basePath}/${callId}/acceptance`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        },
        body: JSON.stringify({})
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else {
          this.resetAcceptCallUi();
          this.showMessage("Accepted, but no redirect URL was returned. Open the call from Calls.");
        }
      } else {
        this.resetAcceptCallUi();
        const msg = data.error || `Could not accept call (${response.status})`;
        this.showMessage(msg);
      }
    } catch (error) {
      console.error("IncomingCall: Error accepting call", error);
      this.resetAcceptCallUi();
      this.showMessage("Could not accept call. Check your connection and try again.");
    }
  }

  resetAcceptCallUi() {
    if (this.hasAcceptButtonTarget) {
      this.acceptButtonTarget.disabled = false;
      this.acceptButtonTarget.textContent = "Accept";
    }
    if (this.hasDeclineButtonTarget) {
      this.declineButtonTarget.disabled = false;
    }
  }

  async decline(event) {
    event.preventDefault();
    event.stopPropagation();

    const callId = event.target?.dataset?.callId || this.currentCallId;
    if (!callId) {
      console.error("IncomingCall: No call ID to decline");
      return;
    }

    try {
      const basePath = this.callsBasePath();
      await fetch(`${basePath}/${callId}/declination`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content
        },
        body: JSON.stringify({})
      });
    } catch (error) {
      console.error("IncomingCall: Error declining call", error);
    }

    this.hideNotification();
  }

  setModalCallerLines(lines) {
    const primary = lines.primary || "";
    const secondary = lines.secondary || "";
    if (this.hasFromNumberTarget) {
      if (primary) {
        this.fromNumberTarget.textContent = primary;
        this.fromNumberTarget.classList.remove("hidden");
      } else {
        this.fromNumberTarget.textContent = "";
        this.fromNumberTarget.classList.add("hidden");
      }
    }
    if (this.hasCustomerNameTarget) {
      if (secondary) {
        this.customerNameTarget.textContent = secondary;
        this.customerNameTarget.classList.remove("hidden");
      } else {
        this.customerNameTarget.textContent = "";
        this.customerNameTarget.classList.add("hidden");
      }
    }
  }

  hideNotification() {
    // Stop ringing sound
    this.stopRingingSound();

    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    if (this.hasNotificationTarget) {
      this.notificationTarget.classList.add("hidden");
      this.notificationTarget.classList.remove("flex");
    }

    this.currentCallId = null;
    this.setModalCallerLines({ primary: "", secondary: "" });

    // Re-enable buttons
    if (this.hasAcceptButtonTarget) {
      this.acceptButtonTarget.disabled = false;
      this.acceptButtonTarget.innerHTML = `
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
        </svg>
        Answer
      `;
    }
    if (this.hasDeclineButtonTarget) {
      this.declineButtonTarget.disabled = false;
    }
  }

  playNotificationSound() {
    // Stop any existing ringing sound
    this.stopRingingSound();

    // Create a phone-like ringing sound using Web Audio API
    // Phone ring pattern: dual-tone ring (440Hz + 480Hz) for 0.4s, pause 0.2s, ring again 0.4s, pause 2s, repeat
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const playRing = () => {
        if (!this.audioContext || this.audioContext.state === "closed") return;
        
        const now = this.audioContext.currentTime;
        
        // Create two oscillators for dual-tone ringing (like a real phone)
        const osc1 = this.audioContext.createOscillator();
        const osc2 = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        osc1.frequency.value = 440; // A4 note
        osc2.frequency.value = 480; // B4 note
        osc1.type = "sine";
        osc2.type = "sine";
        
        osc1.connect(gainNode);
        osc2.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        // Envelope: quick attack, sustain, quick release
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.25, now + 0.05); // Attack
        gainNode.gain.setValueAtTime(0.25, now + 0.35); // Sustain
        gainNode.gain.linearRampToValueAtTime(0, now + 0.4); // Release
        
        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 0.4);
        osc2.stop(now + 0.4);
      };
      
      // Play first ring immediately
      playRing();
      
      // Play second ring after 0.6s (first ring 0.4s + pause 0.2s)
      this.ringTimeout1 = setTimeout(() => {
        playRing();
      }, 600);
      
      // Then repeat the pattern every 3 seconds (0.4s + 0.2s + 0.4s + 2s = 3s)
      this.ringInterval = setInterval(() => {
        playRing();
        // Second ring in the pattern
        setTimeout(() => {
          if (this.ringInterval) { // Only if still ringing
            playRing();
          }
        }, 600);
      }, 3000);
      
    } catch (error) {
      console.warn("IncomingCall: Could not play notification sound", error);
    }
  }

  stopRingingSound() {
    // Stop the ringing interval
    if (this.ringInterval) {
      clearInterval(this.ringInterval);
      this.ringInterval = null;
    }
    
    // Clear timeouts
    if (this.ringTimeout1) {
      clearTimeout(this.ringTimeout1);
      this.ringTimeout1 = null;
    }
    
    // Close audio context if it exists
    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close().catch(() => {
        // Ignore errors when closing
      });
      this.audioContext = null;
    }
  }

  showBrowserNotification(data) {
    if ("Notification" in window && Notification.permission === "granted") {
      const lines = callerLinesFromData(data);
      const body = lines.primary
        ? (lines.secondary ? `${lines.primary} · ${lines.secondary}` : lines.primary)
        : (lines.secondary || "Incoming call");
      new Notification("Incoming Call", {
        body,
        icon: "/favicon.ico",
        tag: `call-${data.call_id}`,
        requireInteraction: true
      });
    }
  }

  showMessage(message) {
    const flashContainer = document.getElementById("flash");
    if (flashContainer) {
      const flashDiv = document.createElement("div");
      flashDiv.className = "app-toast app-toast--info";
      flashDiv.setAttribute("role", "status");
      flashDiv.setAttribute("aria-live", "polite");
      flashDiv.innerHTML = `
        <div class="app-toast__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 11v6m0-9v.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"></path></svg>
        </div>
        <div class="app-toast__content"><div class="app-toast__message"></div></div>
      `;
      flashDiv.querySelector(".app-toast__message").textContent = message;
      flashContainer.appendChild(flashDiv);

      setTimeout(() => {
        flashDiv.classList.add("dismissible--closing");
        setTimeout(() => flashDiv.remove(), 300);
      }, 5000);
    }
  }

  getCurrentUserId() {
    // Try to get current user ID from meta tag or data attribute
    const userIdMeta = document.querySelector("meta[name=\"user-id\"]");
    if (userIdMeta) {
      return parseInt(userIdMeta.content);
    }
    return null;
  }

  callsBasePath() {
    const orgIdentifier = this.hasOrganizationIdentifierValue ? this.organizationIdentifierValue : null;
    const locale = this.hasLocaleValue ? this.localeValue : (document.documentElement.getAttribute("lang") || null);

    if (orgIdentifier && locale) {
      return `/${orgIdentifier}/${locale}/calls`;
    }
    if (orgIdentifier) {
      return `/${orgIdentifier}/calls`;
    }
    if (locale) {
      return `/${locale}/calls`;
    }
    return "/calls";
  }

}
